import React, { useState, FormEvent, ChangeEvent } from 'react';
import { Form, Button, Alert, Image } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import { validateEmail, containsEmoji } from '../../../utils/Utils';
import { ApiError } from '../../../types/errors';
import './Login.css';

interface ForgotPasswordErrors {
  email?: string;
  auth?: string;
}

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [errors, setErrors] = useState<ForgotPasswordErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const newErrors: ForgotPasswordErrors = {};
    if (!email) newErrors.email = 'メールアドレスを入力してください。';
    else if (!validateEmail(email)) newErrors.email = '不正なメールアドレスです。';
    else if (containsEmoji(email)) newErrors.email = '絵文字は使用できません。';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await axios.post('/forgot-password', { email });
      navigate('/forgot-password-result', { state: { email } });
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      if (axiosError.response?.data) {
        setErrors({ auth: axiosError.response.data.message || 'パスワードリセットリクエストに失敗しました' });
      } else {
        setErrors({ auth: 'サーバーに接続できません。ネットワーク接続を確認してください。' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="blank-container">
      <div className="blank-content">
        <div className="login-form-container">
          <div className="text-center mb-4">
            <Image 
              src="/logo.svg" 
              alt="ESF Logo" 
              className="logo-image" 
            />
            <h4>パスワードリセット</h4>
          </div>

          <p className="forgot-password-text mb-4">
            パスワードをリセットするURLを送るメールアドレスを入力し送信してください。
          </p>

          {errors.auth && <Alert variant="danger">{errors.auth}</Alert>}

          <Form onSubmit={handleSubmit} className="login-form">
            <Form.Group className="mb-4">
              <Form.Control
                type="text"
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                isInvalid={!!errors.email}
                placeholder="メールアドレス"
                className="login-input"
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
              <div className="d-flex justify-content-between mt-2">
                <small className="text-muted">例: xxxxxxxxx@xxxxx.com</small>
              </div>
            </Form.Group>

            <div className="login-button-container">
              <Button 
                variant="success" 
                type="submit" 
                className="login-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? '送信中...' : '送信'}
              </Button>
            </div>
          </Form>

          <div className="text-center mt-4">
            <p>
              <Link to="/" className="backlogin-link">ログイン画面に戻る</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
